/**
 * CR518 Addendum / CR523E: S1.4 Cropper Prompt Pack Selector
 * Queries prompt_packs (pack_kind=WORKFLOW, usage_stage=S1_4_CROPPER)
 * Defaults to app_scan_settings.s1_4_cropper_prompt_pack_id
 * CR523E: Validates that selected pack has items with stage=CROP_SNIPPET
 */
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Scissors, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PackOption {
  id: string;
  name: string;
  version: number;
  is_active: boolean;
}

interface S14CropperPackSelectorProps {
  selectedPackId: string | null;
  onSelect: (packId: string | null) => void;
}

export function S14CropperPackSelector({ selectedPackId, onSelect }: S14CropperPackSelectorProps) {
  const [options, setOptions] = useState<PackOption[]>([]);
  const [loading, setLoading] = useState(true);
  // CR523E: Stage readiness state
  const [stageReady, setStageReady] = useState<boolean | null>(null);
  const [stageCounts, setStageCounts] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    async function fetchOptions() {
      setLoading(true);

      const [packsResult, settingsResult] = await Promise.all([
        supabase
          .from("prompt_packs")
          .select("id, name, version, is_active")
          .eq("pack_kind", "WORKFLOW")
          .eq("usage_stage", "S1_4_CROPPER")
          .is("archived_at", null)
          .order("is_active", { ascending: false })
          .order("name", { ascending: true }),
        supabase
          .from("app_scan_settings")
          .select("s1_4_cropper_prompt_pack_id")
          .eq("singleton_id", true)
          .maybeSingle(),
      ]);

      const packs = (packsResult.data ?? []) as PackOption[];
      setOptions(packs);

      // Auto-select: use scan settings default, else first active, else first
      if (!selectedPackId && packs.length > 0) {
        const defaultId = (settingsResult.data as any)?.s1_4_cropper_prompt_pack_id;
        const match = defaultId ? packs.find(p => p.id === defaultId) : null;
        const pick = match || packs.find(p => p.is_active) || packs[0];
        onSelect(pick.id);
      }

      setLoading(false);
    }
    fetchOptions();
  }, []);

  // CR523E: Validate stage readiness when pack selection changes
  useEffect(() => {
    if (!selectedPackId) {
      setStageReady(null);
      setStageCounts(null);
      return;
    }

    async function checkStageReadiness() {
      const { data: items } = await supabase
        .from("prompt_pack_items")
        .select("stage")
        .eq("prompt_pack_id", selectedPackId!)
        .eq("enabled", true);

      if (!items) {
        setStageReady(false);
        setStageCounts(null);
        return;
      }

      const counts: Record<string, number> = {};
      for (const item of items) {
        const s = (item as any).stage || "UNKNOWN";
        counts[s] = (counts[s] || 0) + 1;
      }
      setStageCounts(counts);
      setStageReady((counts["CROP_SNIPPET"] ?? 0) > 0);
    }
    checkStageReadiness();
  }, [selectedPackId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Scissors className="h-3.5 w-3.5" />
        <span className="text-xs">Loading cropper packs...</span>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <Label className="text-xs text-destructive flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" />
          Cropper pack (Required)
        </Label>
        <span className="text-xs text-destructive">No S1.5 Cropper packs found — S1.5 will fail</span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground flex items-center gap-1.5 whitespace-nowrap">
          <Scissors className="h-3.5 w-3.5" />
          Cropper pack
          <Badge variant="outline" className="text-[10px] px-1 py-0 border-destructive/50 text-destructive">Required</Badge>
        </Label>
        <Select
          value={selectedPackId || ""}
          onValueChange={(v) => onSelect(v || null)}
        >
          <SelectTrigger className="h-7 text-xs w-[260px]">
            <SelectValue placeholder="Select cropper pack..." />
          </SelectTrigger>
          <SelectContent>
            {options.map(opt => (
              <SelectItem key={opt.id} value={opt.id} className="text-xs">
                <div className="flex items-center gap-2">
                  <span>{opt.name}</span>
                  <span className="text-muted-foreground">published v{opt.version}</span>
                  {opt.is_active && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0">Active</Badge>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* CR523E: Stage readiness indicator */}
      {selectedPackId && stageReady !== null && (
        <div className="flex items-center gap-1.5 ml-5 text-[11px]">
          {stageReady ? (
            <>
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span className="text-muted-foreground">has CROP_SNIPPET stage</span>
            </>
          ) : (
            <>
              <XCircle className="h-3 w-3 text-destructive" />
              <span className="text-destructive">missing CROP_SNIPPET stage — S1.5 will fail</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
